import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { Wave, Spark } from "../ui/decor";
import { joinWaitlist } from "../../lib/api";
import { track } from "../../lib/analytics";

type Status = "idle" | "loading" | "success" | "error";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Waitlist() {
  const [status, setStatus] = useState<Status>("idle");
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "loading") return;

    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setStatus("error");
      setErrorMsg("Enter a valid email address.");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const result = await joinWaitlist(value, { source: "waitlist" });
      if ("notConfigured" in result) {
        setStatus("error");
        setErrorMsg("Sign-ups open soon.");
        return;
      }
      setStatus("success");
      track("waitlist_join", { already: result.already ?? false });
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error && err.message
          ? err.message
          : "Something went wrong — try again."
      );
    }
  }

  return (
    <section id="waitlist" className="relative overflow-hidden bg-violet-deep">
      {/* Melt into the cream page above and below */}
      <Wave fill="#FFF6EC" flip />

      {/* Playful floating accents on the flat violet block. */}
      <Spark
        fill="#FFC93C"
        className="absolute left-[6%] top-24 hidden animate-float md:block"
      />
      <Spark
        fill="#CCFBF1"
        className="absolute bottom-28 right-[7%] hidden h-10 w-10 animate-float md:block"
      />

      <div className="container-x py-16 sm:py-24">
        <SectionHeading
          align="center"
          light
          eyebrow="Early access"
          title={
            <>
              Be first to{" "}
              <span className="marker text-navy">bank calories</span>
            </>
          }
          sub="FitBuddy is coming to iOS and Android. Join the early list and we'll email you the moment it lands — no spam, just the launch."
        />

        <div className="mx-auto mt-10 max-w-xl">
          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98, rotate: -2 }}
              animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="card-pop relative flex flex-col items-center px-6 py-10 text-center"
              role="status"
              aria-live="polite"
            >
              {/* Celebration stickers slapped on the corners. */}
              <Spark
                fill="#FFC93C"
                className="absolute -left-4 -top-4 rotate-12"
              />
              <Spark
                fill="#FF6B5E"
                className="absolute -bottom-3 -right-3 h-7 w-7 -rotate-12"
              />
              <span className="flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-navy bg-sun text-navy shadow-pop-xs">
                <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
              </span>
              <h3 className="display mt-6 text-2xl text-navy">
                You&apos;re on the list.
              </h3>
              <p className="mt-3 text-charcoal/75">
                We&apos;ll email you the second FitBuddy is live. Every gram
                you lose is about to start earning.
              </p>
            </motion.div>
          ) : (
            <Reveal>
              <form onSubmit={handleSubmit} noValidate>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <label htmlFor="waitlist-email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="waitlist-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === "error") {
                        setStatus("idle");
                        setErrorMsg("");
                      }
                    }}
                    aria-invalid={status === "error"}
                    aria-describedby={
                      status === "error" ? "waitlist-error" : undefined
                    }
                    className="w-full flex-1 rounded-full border-[3px] border-navy bg-white px-5 py-3.5 text-navy placeholder-charcoal/50 outline-none transition focus:ring-2 focus:ring-sun focus:ring-offset-2 focus:ring-offset-violet-deep"
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="btn-sun shrink-0 justify-center px-7 py-3.5 text-base disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2
                          className="h-4 w-4 animate-spin"
                          aria-hidden="true"
                        />
                        <span>Joining…</span>
                      </>
                    ) : (
                      "Join the list"
                    )}
                  </button>
                </div>

                {status === "error" && (
                  <p
                    id="waitlist-error"
                    className="mt-3 inline-block rounded-full border-2 border-navy bg-white px-4 py-1.5 text-sm font-bold text-navy"
                    role="alert"
                  >
                    {errorMsg}
                  </p>
                )}

                <p className="mt-5 flex items-center justify-center gap-2 text-sm text-white/90">
                  <ShieldCheck
                    className="h-4 w-4 text-sun"
                    aria-hidden="true"
                  />
                  We&apos;ll only email you about launch. Unsubscribe
                  anytime.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </div>

      <Wave fill="#FFF6EC" />
    </section>
  );
}
